/**
 * Pure rules for Transfer Property / Transfer Company wizard tabs and fields.
 *
 * Angular source: `frontend-app/.../transfer-process.component.ts` + template validators.
 * React UI: `TransferProcessPage.tsx` — keep logic here so unit tests mirror production without mounting the page.
 */

import type {
  PropertyStaffRow,
  TransferEmployeeSubTab,
  TransferFragment,
  TransferTabId,
} from "../types"

/** Template `numberPattern` / HTML `[pattern]` for unit counts (digits only). */
export const TRANSFER_UNITS_COUNT_PATTERN = /^[0-9]+$/

/** Resolves user id from `/api/property_staff` rows (`user_id` or `fk_user_id`). */
export function resolveTransferStaffUserId(row: PropertyStaffRow): number | undefined {
  const raw = row.user_id ?? row.fk_user_id
  if (raw == null || raw === "") return undefined
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return n
}

/**
 * Narrows property-staff rows to `company_staff` from GET `/api/company/:id` when that list is non-empty.
 * Optional helper for custom UIs; Angular **transfer** uses unfiltered `GET /api/property_staff?company_id=` only.
 */
export function filterPropertyStaffRowsByCompanyStaff(
  rows: PropertyStaffRow[],
  company: Record<string, unknown>,
): PropertyStaffRow[] {
  const cs = company["company_staff"]
  if (!Array.isArray(cs) || cs.length === 0) return rows
  const ids = new Set<number>()
  for (const item of cs) {
    if (item && typeof item === "object" && "user_id" in item) {
      const n = Number((item as { user_id?: unknown }).user_id)
      if (Number.isFinite(n) && n > 0) ids.add(n)
    }
  }
  if (ids.size === 0) return rows
  return rows.filter((row) => {
    const uid = resolveTransferStaffUserId(row)
    return uid !== undefined && ids.has(uid)
  })
}

/** Hidden control — `Validators.required` on `fk_company_id` (Angular) / RHF validate (React). */
export function validateFkCompanyId(v: unknown): true | string {
  return v && String(v).trim() !== "" ? true : "Company Selection is required."
}

/** Split flow: Old company shows required star — enforce matching validator. */
export function validateOldCompanyIdForFragment(
  v: unknown,
  fragment: TransferFragment | null | undefined,
): true | string {
  if (fragment !== "split_property") return true
  return validateFkCompanyId(v)
}

/**
 * `property_id` required only for `merge_property` and `transfer_resident` (dynamically added in Angular `loadFragment`).
 */
export function validatePropertyIdForFragment(
  v: unknown,
  fragment: TransferFragment | null | undefined,
): true | string {
  if (fragment !== "merge_property" && fragment !== "transfer_resident") return true
  return v && String(v).trim() !== "" ? true : "Property is required"
}

export const TRANSFER_FULL_TAB_ORDER: readonly TransferTabId[] = [
  "propertyinfo",
  "contactsinfo",
  "residentinfo",
  "summaryinfo",
]

export function transferWizardTabOrder(showEmployeeTab: boolean): TransferTabId[] {
  return showEmployeeTab ? [...TRANSFER_FULL_TAB_ORDER] : ["propertyinfo", "residentinfo", "summaryinfo"]
}

/**
 * Employee tab cannot be opened from Property step until requirements met
 * (Angular nav: Employee only gated from Property tab; Residents need empDone; Summary needs residentDone).
 */
/**
 * Employee Transfer sub-step has no `staffList` rows and no pending remove/transfer buckets.
 * Matches Angular `*ngIf="!(removestaffList?.length || transferstaffList?.length || staffList?.length)"`.
 */
export function employeeTransferStepIsVacant(opts: {
  staffListLength: number
  removestaffListLength: number
  transferstaffListLength: number
}): boolean {
  const { staffListLength, removestaffListLength, transferstaffListLength } = opts
  return !(staffListLength || removestaffListLength || transferstaffListLength)
}

export function employeeTabDisabledFromProperty(opts: {
  selectedTab: TransferTabId
  fkCompanyId: string
  fragment: TransferFragment | null
  infoDone: boolean
}): boolean {
  const { selectedTab, fkCompanyId, fragment, infoDone } = opts
  if (selectedTab !== "propertyinfo") return false
  const noCompany = !String(fkCompanyId ?? "").trim()
  const companyOptionalFragment =
    fragment === "transfer_resident" || fragment === "merge_property" || fragment === "transfer_company"
  return (noCompany && !companyOptionalFragment) || !infoDone
}

/**
 * Primary "Next" control disabled state (property + contacts steps; residents/summary use other rules in-page).
 */
export function computeTransferNextDisabled(opts: {
  selectedTab: TransferTabId
  selectedSubTab?: TransferEmployeeSubTab
  formIsValid: boolean
  fragment: TransferFragment | null
  newPropertyNameTrimmed: string
  commitDone: boolean
  verifyDone: boolean
  removestaffListLength: number
  transferstaffListLength: number
  staffListLength: number
  addedstaffListLength: number
  /** Residents tab: block Finish until Transfer ran when rows exist. */
  residentsLength?: number
  applicationsLength?: number
  residentcount?: number
  applicantcount?: number
}): boolean {
  const {
    selectedTab,
    formIsValid,
    fragment,
    newPropertyNameTrimmed,
    commitDone,
    verifyDone,
    removestaffListLength,
    transferstaffListLength,
    staffListLength,
    addedstaffListLength,
    residentsLength = 0,
    applicationsLength = 0,
    residentcount = 0,
    applicantcount = 0,
  } = opts

  if (
    selectedTab === "propertyinfo" &&
    (!formIsValid || (fragment === "split_property" && !newPropertyNameTrimmed))
  ) {
    return true
  }

  if (selectedTab === "contactsinfo") {
    // Angular Next disabled: pending commit with staff rows, or pending verify with transfer/add rows.
    // Empty Add & Verify auto-sets verifyDone on Next (see nextTab).
    if (
      (!commitDone &&
        (removestaffListLength > 0 || transferstaffListLength > 0 || staffListLength > 0)) ||
      (!verifyDone && (transferstaffListLength > 0 || addedstaffListLength > 0))
    ) {
      return true
    }
  }

  // transfer_resident / split: Finish requires Transfer when there is anyone to move.
  if (
    selectedTab === "residentinfo" &&
    (fragment === "transfer_resident" || fragment === "split_property") &&
    (residentsLength > 0 || applicationsLength > 0) &&
    residentcount + applicantcount === 0
  ) {
    return true
  }

  return false
}

/**
 * Payments block visible for these flows (Angular: hidden only for `transfer_resident`; `split_property` uses two
 * side-by-side payment groups — root fields vs `newpropertyinfo` — see transfer-process.component.html).
 * Paper address fields show when `payment_method === 'PAPER_CHECK'` only.
 */
export function showPaymentsSection(fragment: TransferFragment | null | undefined): boolean {
  return fragment !== "transfer_resident"
}

/** Stepper label for first tab (Company vs Property wording) — unnumbered, matches entity edit tab style. */
export function transferFirstStepLabel(fragment: TransferFragment | null | undefined): string {
  return fragment === "transfer_company" ? "Company Info" : "Property Info"
}

/** Human-readable labels for pending-process conflict messages. */
export const TRANSFER_PROCESS_DISPLAY_LABELS: Record<TransferFragment, string> = {
  transfer_company: "Transfer Company",
  transfer_property: "Transfer Property",
  transfer_resident: "Transfer Residents",
  merge_property: "Merge Property",
  split_property: "Split Property",
}

/** GET params to find any in-progress transfer for the current entity (Angular transfer-option parity). */
export function buildPendingTransferLogQueryParams(
  fragment: TransferFragment | null | undefined,
  entityId: string,
): Record<string, string> | null {
  const id = String(entityId ?? "").trim()
  if (!fragment || !id) return null
  if (fragment === "transfer_company") {
    return { status: "Pending", company_id: id }
  }
  return { status: "Pending", property_id: id }
}

/** True when the API already has a pending log for a different transfer type. */
export function pendingTransferProcessConflictsWithFragment(
  pendingProcessName: string | undefined,
  fragment: TransferFragment | null | undefined,
): pendingProcessName is TransferFragment {
  if (!pendingProcessName || !fragment) return false
  if (pendingProcessName === fragment) return false
  return pendingProcessName in TRANSFER_PROCESS_DISPLAY_LABELS
}

/** Card + icon section layout — polished wizard styling (tabs, dialogs, section cards). */
export function usesTransferWizardCardLayout(
  fragment: TransferFragment | null | undefined,
): boolean {
  return (
    fragment === "transfer_company" ||
    fragment === "transfer_property" ||
    fragment === "transfer_resident" ||
    fragment === "merge_property" ||
    fragment === "split_property"
  )
}

/** @deprecated Use `usesTransferWizardCardLayout` */
export const usesTransferPropertyInfoCardLayout = usesTransferWizardCardLayout

function collectPropertyIdCandidates(res: Record<string, unknown>): string[] {
  const out = new Set<string>()
  const add = (v: unknown) => {
    if (v == null) return
    const s = String(v).trim()
    if (s !== "") out.add(s)
  }
  const scan = (obj: Record<string, unknown>) => {
    add(obj.property_id)
    add(obj.fk_property_id)
    add((obj as { id?: unknown }).id)
  }
  scan(res)
  const nestedKeys = ["property", "data", "result", "payload"] as const
  for (const key of nestedKeys) {
    const nested = res[key]
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      scan(nested as Record<string, unknown>)
    }
  }
  return [...out]
}

function collectCompanyIdCandidates(res: Record<string, unknown>): string[] {
  const out = new Set<string>()
  const add = (v: unknown) => {
    if (v == null) return
    const s = String(v).trim()
    if (s !== "") out.add(s)
  }
  const scan = (obj: Record<string, unknown>) => {
    add(obj.company_id)
    // Intentionally omit `fk_company_id` — it is a *pointer* (e.g. parent org), not the row's own id
    // (unlike `fk_property_id` on a property, which is still this property’s key in some API shapes).
    // Including it would reject Angular-parity "ids omitted" bodies that still send fk_company_id for other reasons.
    add((obj as { id?: unknown }).id)
  }
  scan(res)
  const nestedKeys = ["company", "data", "result", "payload"] as const
  for (const key of nestedKeys) {
    const nested = res[key]
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      scan(nested as Record<string, unknown>)
    }
  }
  return [...out]
}

/**
 * When the JSON omits `property_id` (still a 200 with a usable row), match Angular: trust GET success.
 * Rejects a lone placeholder like `{ property_name: "X" }` with no company/address/payee.
 */
function propertyTransferResponseLooksLikeRow(o: Record<string, unknown>): boolean {
  const name =
    o.property_name != null && String(o.property_name).trim() !== ""
      ? String(o.property_name).trim()
      : ""
  const hasCompany =
    o.fk_company_id != null ||
    (o.company_name != null && String(o.company_name).trim() !== "")
  const hasAddr =
    o.address1 != null || o.city != null || o.state != null || o.zip != null || o.country != null
  const hasPayee = o.payee_name != null
  if (name && hasCompany) return true
  if (name && hasAddr) return true
  if (name && hasPayee) return true
  return false
}

function companyTransferResponseLooksLikeRow(o: Record<string, unknown>): boolean {
  const cn =
    o.company_name != null && String(o.company_name).trim() !== ""
      ? String(o.company_name).trim()
      : ""
  const hasAddr = o.address1 != null || o.city != null || o.state != null
  const hasContact = o.phone != null || o.email != null
  const hasPayee = o.payee_name != null
  if (cn && (hasAddr || hasContact)) return true
  if (cn && (o.company_id != null || o.fk_company_id != null)) return true
  if (cn && hasPayee) return true
  return false
}

/**
 * After `GET /api/property/:id` — Angular only continues when the request succeeds with a real row
 * (`getPropertyDetails` → subscribe success). Reject empty `{}` or id mismatch (treat as unusable).
 * Accepts nested `data` / `property` and `fk_property_id` when the backend shape differs from a flat row.
 */
export function propertyTransferGetResponseValid(
  res: Record<string, unknown> | null | undefined,
  routePropertyId: string,
): boolean {
  if (!res || typeof res !== "object") return false
  if (Object.keys(res).length === 0) return false
  const rid = String(routePropertyId ?? "").trim()
  if (!rid) return false
  const candidates = collectPropertyIdCandidates(res)
  if (candidates.length > 0) {
    return candidates.some((c) => c === rid)
  }
  return propertyTransferResponseLooksLikeRow(res)
}

/**
 * After `GET /api/company/:id` for `#transfer_company`.
 * Angular `getCompanyDetails()` uses the response on HTTP success without requiring ids in JSON.
 * When ids are present, they must match the route param; when omitted, non-empty `company_name` is enough.
 */
export function companyTransferGetResponseValid(
  res: Record<string, unknown> | null | undefined,
  routeCompanyId: string,
): boolean {
  if (!res || typeof res !== "object") return false
  if (Object.keys(res).length === 0) return false
  const rid = String(routeCompanyId ?? "").trim()
  if (!rid) return false
  const candidates = collectCompanyIdCandidates(res)
  if (candidates.length > 0) {
    return candidates.some((c) => c === rid)
  }
  const name = res.company_name != null ? String(res.company_name).trim() : ""
  if (name !== "") return true
  return companyTransferResponseLooksLikeRow(res)
}
